import { Box, FlexboxGrid, Heading, Icon, IconSvg, style } from '@do/walrus';
import React from 'react';
import styled from 'styled-components';
import Image from '../Image';
import copy from '../copy';

const TUTORIAL_MIN_WIDTH = '288px';
const TUTORIAL_MAX_WIDTH = '333px';

export interface FeaturedTutorial {
  title: string;
  link: string;
}

const StyledGrid = styled(FlexboxGrid)`
  box-sizing: border-box;
`;

const TutorialItem = styled.div`
  position: relative;
  border-radius: ${style.vars.borderRadius.base};
  min-width: ${TUTORIAL_MIN_WIDTH};
  max-width: ${TUTORIAL_MAX_WIDTH};
`;

const TutorialItemContent = styled.div`
  position: absolute;
  padding: ${style.vars.space['4']};
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const AllTutorialsItem = styled.div`
  background-image: url('/static/tutorial-bg.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  padding: ${style.vars.space['5']};
  border-radius: ${style.vars.borderRadius.base};
`;

const TutorialLinkText = styled.span`
  color: white;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${style.vars.fontWeight.bold};
`;

const ExternalTutorialsLink = styled.a`
  display: flex;
  align-items: center;
  text-align: center;
  text-decoration: underline;
  margin-top: ${style.vars.space['2']};
  margin-bottom: ${style.vars.space['4']};

  ${IconSvg} {
    margin-left: ${style.vars.space['1']};
  }
`;

export const TutorialCard: React.FC<{
  tutorial: FeaturedTutorial;
  onTutorialClick?: () => void;
}> = ({ tutorial, onTutorialClick }) => (
  <a
    href={tutorial.link}
    onClick={onTutorialClick}
    title={tutorial.title}
    target="_blank"
    rel="noopener noreferrer"
  >
    <TutorialItem>
      {typeof document !== 'undefined' && (
        <Image
          src={`${document.location.origin}/static/tutorial-bg.png`}
          layout="fill"
          objectFit="contain"
          objectPosition="center"
          alt={copy.tutorials.featuredTutorial}
        />
      )}
      <TutorialItemContent>
        <TutorialLinkText>
          {copy.tutorials.featuredTutorial}
          <Icon icon="external-link" />
        </TutorialLinkText>
        <Box mt={3}>
          <Heading level="h3" color="white" scale="md">
            {tutorial.title}
          </Heading>
        </Box>
      </TutorialItemContent>
    </TutorialItem>
  </a>
);

export const AllTutorialsCard: React.FC<{
  name: string;
  tutorialsLink: string;
  onTutorialClick?: () => void;
}> = ({ name, tutorialsLink, onTutorialClick }) => (
  <a
    href={tutorialsLink}
    onClick={onTutorialClick}
    title={copy.tutorials.seeOurTutorials(name)}
    target="_blank"
    rel="noopener noreferrer"
  >
    <AllTutorialsItem>
      <TutorialLinkText>
        {copy.tutorials.seeOurTutorials(name)}
        <Icon icon="external-link" />
      </TutorialLinkText>
    </AllTutorialsItem>
  </a>
);

export const SeeAllTutorialsLink: React.FC<{
  name: string;
  tutorialsLink: string;
  onTutorialClick?: () => void;
}> = ({ name, tutorialsLink, onTutorialClick }) => (
  <ExternalTutorialsLink
    href={tutorialsLink}
    onClick={onTutorialClick}
    target="_blank"
    rel="noopener noreferrer"
  >
    {copy.tutorials.seeAllTutorials(name)}
    <Icon icon="external-link" />
  </ExternalTutorialsLink>
);

export const FeaturedTutorialSection: React.FC<{
  name: string;
  tutorial?: FeaturedTutorial;
  tutorialsLink: string;
  onTutorialClick?: () => void;
}> = ({ name, tutorial, tutorialsLink, onTutorialClick }) => {
  if (!tutorial) {
    return (
      <AllTutorialsCard
        name={name}
        tutorialsLink={tutorialsLink}
        onTutorialClick={onTutorialClick}
      />
    );
  }

  return (
    <>
      <TutorialCard tutorial={tutorial} onTutorialClick={onTutorialClick} />
      <SeeAllTutorialsLink
        name={name}
        tutorialsLink={tutorialsLink}
        onTutorialClick={onTutorialClick}
      />
    </>
  );
};

export const TutorialsGrid: React.FC<{
  name: string;
  tutorialsLink: string;
  featuredTutorials: FeaturedTutorial[];
  onTutorialClick?: () => void;
}> = ({ name, tutorialsLink, featuredTutorials, onTutorialClick }) => {
  if (!featuredTutorials || featuredTutorials.length === 0) {
    return (
      <StyledGrid>
        <FlexboxGrid.Col sm={10}>
          <AllTutorialsCard
            name={name}
            tutorialsLink={tutorialsLink}
            onTutorialClick={onTutorialClick}
          />
        </FlexboxGrid.Col>
      </StyledGrid>
    );
  }

  return (
    <StyledGrid>
      {featuredTutorials?.map((tutorial) => (
        <FlexboxGrid.Col sm={6} key={tutorial.title}>
          <TutorialCard tutorial={tutorial} onTutorialClick={onTutorialClick} />
        </FlexboxGrid.Col>
      ))}
      <FlexboxGrid.Col sm={12}>
        <SeeAllTutorialsLink
          name={name}
          tutorialsLink={tutorialsLink}
          onTutorialClick={onTutorialClick}
        />
      </FlexboxGrid.Col>
    </StyledGrid>
  );
};
